import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Stack,
} from '@mui/material';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { array, boolean, object, string, type Schema } from 'yup';

import ContentLoader from '@components/ContentLoader';
import {
  FormikDropdownField,
  FormikMultiAutocompleteField,
} from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton';

import { useHealthChart } from '@hooks/useHealthChart';

const messages = defineMessages({
  filter: {
    id: 'filterButton.filter',
    defaultMessage: 'filter',
  },
  reset: {
    id: 'GLOBAL.reset',
    defaultMessage: 'Reset',
  },
  clearFilters: {
    id: 'filterModalContent.clearFilters',
    defaultMessage: 'Clear Filters',
  },
  filterBy: {
    id: 'filterModalContent.filterBy',
    defaultMessage: 'Filter By',
  },
  clinicalStatus: {
    id: 'vitalSigns.clinicalStatus',
    defaultMessage: 'Clinical Status',
  },
  source: {
    id: 'GLOBAL.source',
    defaultMessage: 'Source',
  },
  select: {
    id: 'GLOBAL.select',
    defaultMessage: 'Select',
  },
  clinical: {
    id: 'vitalSigns.clinical',
    defaultMessage: 'Clinical',
  },
  nonClinical: {
    id: 'vitalSigns.nonClinical',
    defaultMessage: 'Non-Clinical',
  },
});

interface FilterButtonDialogProps {
  open: boolean;
  onClose: () => void;
}

type FormValues = {
  sources?: string[] | null;
  clinicalStatus?: boolean | null;
  submit: string | null;
};

const CLINICAL_STATUS_OPTIONS = [
  {
    k: '1',
    v: 'Clinical',
  },
  {
    k: '0',
    v: 'Non-Clinical',
  },
];

export default function FilterButtonDialog({
  open,
  onClose: handleClose,
}: FilterButtonDialogProps) {
  const { formatMessage } = useIntl();
  const {
    clinicalStatus,
    completeData,
    setSourceAndClinicalFilters,
    sources,
    loading,
  } = useHealthChart();
  const sourceSet = new Set(completeData.map(({ source }) => source));

  const initialValues: FormValues = {
    sources: sources ?? [],
    clinicalStatus: typeof clinicalStatus === 'boolean' ? clinicalStatus : null,
    submit: null,
  };

  const validationSchema: Schema<Partial<FormValues>> = object().shape({
    sources: array().of(string().required()),
    clinicalStatus: boolean().oneOf([true, false]).nullable(),
  });

  const handleSubmit = (
    values: FormValues,
    { setErrors }: FormikHelpers<FormValues>
  ) => {
    if (!values.sources?.length && typeof values.clinicalStatus !== 'boolean') {
      setErrors({ submit: 'Select a source or clinical status to filter by' });
    }

    setSourceAndClinicalFilters({
      sources: values.sources ?? undefined,
      clinicalStatus: values.clinicalStatus ?? undefined,
    });
    handleClose();
  };

  const handleReset = () => {
    setSourceAndClinicalFilters({
      sources: undefined,
      clinicalStatus: undefined,
    });

    handleClose();
  };

  return (
    <Dialog maxWidth="xl" onClose={handleClose} open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ errors, setFieldValue, isSubmitting }) => {
          return (
            <Form>
              <DialogTitle>{formatMessage(messages.filterBy)}</DialogTitle>
              <DialogContent>
                <ContentLoader loading={loading}>
                  <Stack my={2}>
                    <FormikMultiAutocompleteField
                      label={formatMessage(messages.source)}
                      name="sources"
                      options={[...sourceSet]}
                    />
                  </Stack>
                  <Stack>
                    <FormikDropdownField
                      label={formatMessage(messages.clinicalStatus)}
                      labelColor="primary.main"
                      name="clinicalStatus"
                      options={CLINICAL_STATUS_OPTIONS}
                      onChange={(value) => {
                        setFieldValue(
                          'clinicalStatus',
                          value === '1' ? true : value === '0' ? false : null
                        );
                      }}
                    />
                  </Stack>

                  {Boolean(errors) && (
                    <FormHelperText>{errors.submit}</FormHelperText>
                  )}
                </ContentLoader>

                <RoundedButton
                  disabled={loading || isSubmitting}
                  onClick={handleReset}
                  variant="white"
                >
                  {formatMessage(messages.reset)}
                </RoundedButton>
                <RoundedButton type="submit" disabled={loading || isSubmitting}>
                  {formatMessage(messages.filter)}
                </RoundedButton>
              </DialogContent>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
